import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Save, X, Loader2, Plus, Tag } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { RequiredLabel } from "@/components/catalog/RequiredLabel";
import { isFieldRequiredOnPublish } from "@/lib/catalog/catalog-required-fields";
import { catalogApi, CreateDigitalResourceRequest, DigitalResource } from "@/lib/api/catalog";
import type { Collection } from "@/lib/api/collections";
import { useCollections } from "@/hooks/useCollections";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { PaidAccessRulesEditor } from "@/components/catalog/PaidAccessRulesEditor";
import { PaidAccessFileReplaceNotice } from "@/components/catalog/PaidAccessFileReplaceNotice";
import { paidAccessApi } from "@/lib/api/paid-access";
import { useAccessControl } from "@/hooks/useAccessControl";
import type { PaidAccessAdminSnapshot } from "@/components/catalog/paid-access-admin.types";
import { PaidAccessVisibilityAlert } from "@/components/catalog/PaidAccessVisibilityAlert";

export function EditDigitalResourcePage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAccessControl();
  const canManagePaidAccess =
    hasPermission("digital.paidAccess.manage") || hasPermission("digital.*");
  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingResource, setLoadingResource] = useState(true);
  const { data: collections = [] } = useCollections(true);
  const [formData, setFormData] = useState<CreateDigitalResourceRequest>({
    title: "",
    description: "",
    authors: [],
    type: "PDF",
    publisher: "",
    publishedDate: "",
    subjects: [],
    tags: [],
    lcClassification: "",
    callNumber: "",
    collectionId: undefined,
    isPublic: false,
    allowDownload: false,
    websiteUrl: "",
  });
  const [authorInput, setAuthorInput] = useState("");
  const [subjectInput, setSubjectInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [isExternalResource, setIsExternalResource] = useState(false);
  const [paidAccessRequired, setPaidAccessRequired] = useState(false);
  const [hasPaidActivity, setHasPaidActivity] = useState(false);
  const [paidAdminSnapshot, setPaidAdminSnapshot] = useState<PaidAccessAdminSnapshot | null>(null);
  const [paidAdminLoaded, setPaidAdminLoaded] = useState(false);
  const [showPublicAccessHint, setShowPublicAccessHint] = useState(false);

  const loadPaidAdminSnapshot = useCallback(async () => {
    if (!id || formData.type !== "PDF" || isExternalResource) {
      setPaidAdminSnapshot(null);
      setHasPaidActivity(false);
      setPaidAdminLoaded(true);
      return;
    }
    try {
      const [rules, purchases, entitlements] = await Promise.all([
        paidAccessApi.getPricingRules(id),
        paidAccessApi.listPurchases(id),
        paidAccessApi.listEntitlements(id),
      ]);
      setPaidAdminSnapshot({ rules, purchases, entitlements });
      setHasPaidActivity(purchases.length > 0 || entitlements.length > 0);
      setPaidAccessRequired(rules.length > 0);
    } catch {
      setPaidAdminSnapshot(null);
      setHasPaidActivity(false);
    } finally {
      setPaidAdminLoaded(true);
    }
  }, [id, formData.type, isExternalResource]);

  useEffect(() => {
    if (id) {
      loadResource();
    }
  }, [id]);

  useEffect(() => {
    void loadPaidAdminSnapshot();
  }, [loadPaidAdminSnapshot]);

  const loadResource = async () => {
    if (!id) return;
    setLoadingResource(true);
    try {
      const resource = await catalogApi.getDigitalResource(id);
      const metadata = (resource.metadata as Record<string, unknown>) || {};
      const external =
        resource.isExternalUrl === true ||
        metadata.isExternalUrl === true ||
        resource.type === "WEBSITE";
      setIsExternalResource(external);
      setFormData({
        title: resource.title,
        description: resource.description || "",
        authors: resource.authors || [],
        type: resource.type as any,
        publisher: resource.publisher || "",
        publishedDate: resource.publishedDate || "",
        subjects: resource.subjects || [],
        tags: resource.tags || [],
        lcClassification: resource.lcClassification || "",
        callNumber: resource.callNumber || "",
        collectionId: resource.collectionId || undefined,
        isPublic: resource.isPublic || false,
        allowDownload: metadata.allowDownload === true,
        websiteUrl: external
          ? resource.fileUrl || ""
          : typeof metadata.websiteUrl === "string"
            ? metadata.websiteUrl
            : "",
      });
    } catch (error: any) {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to load resource",
      });
      setTimeout(() => setAlert(null), 5000);
    } finally {
      setLoadingResource(false);
    }
  };

  const addAuthor = () => {
    if (authorInput.trim() && !formData.authors?.includes(authorInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        authors: [...(prev.authors || []), authorInput.trim()],
      }));
      setAuthorInput("");
    }
  };

  const removeAuthor = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      authors: prev.authors?.filter((_, i) => i !== index) || [],
    }));
  };

  const addSubject = () => {
    if (subjectInput.trim() && !formData.subjects?.includes(subjectInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        subjects: [...(prev.subjects || []), subjectInput.trim()],
      }));
      setSubjectInput("");
    }
  };

  const removeSubject = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      subjects: prev.subjects?.filter((_, i) => i !== index) || [],
    }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const removeTag = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    if (!formData.title.trim()) {
      setAlert({
        type: "error",
        message: "Title is required",
      });
      setTimeout(() => setAlert(null), 5000);
      return;
    }

    setLoading(true);
    try {
      const { websiteUrl, ...rest } = formData;
      const payload: Partial<CreateDigitalResourceRequest> & { fileUrl?: string } = { ...rest };
      if (isExternalResource) {
        if (!websiteUrl?.trim() || !/^https?:\/\/.+/i.test(websiteUrl.trim())) {
          setAlert({ type: "error", message: "A valid website URL is required" });
          setTimeout(() => setAlert(null), 5000);
          setLoading(false);
          return;
        }
        payload.fileUrl = websiteUrl.trim();
      }
      await catalogApi.updateDigitalResource(id, payload);
      setAlert({
        type: "success",
        message: "Digital resource updated successfully",
      });
      setTimeout(() => {
        setAlert(null);
        navigate("/catalog");
      }, 2000);
    } catch (error: any) {
      setAlert({
        type: "error",
        message: error.response?.data?.message || "Failed to update resource",
      });
      setTimeout(() => setAlert(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  if (loadingResource) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const resourceTitle = formData.title.trim() || "Digital resource";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Edit Digital Resource"
        description="Update the details of this digital resource"
        titleSize="md"
        showSearch={false}
        breadcrumbItems={[
          { label: "Catalog", href: "/catalog" },
          ...(id ? [{ label: resourceTitle, href: `/catalog/digital/${id}` }] : []),
          { label: "Edit" },
        ]}
      />

      {/* Alert */}
      {alert && (
        <Alert
          className={
            alert.type === "success"
              ? "bg-[#16A34A]/10 border-[#16A34A]"
              : "bg-[#DC3545]/10 border-[#DC3545]"
          }
        >
          <AlertDescription
            className={alert.type === "success" ? "text-[#16A34A]" : "text-[#DC3545]"}
          >
            {alert.message}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <RequiredLabel
                htmlFor="title"
                required={isFieldRequiredOnPublish("E_RESOURCE", "title")}
              >
                Title
              </RequiredLabel>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="Enter resource title"
                className="mt-1.5"
                required
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Enter resource description"
                rows={4}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <RequiredLabel
                  htmlFor="type"
                  required={isFieldRequiredOnPublish("E_RESOURCE", "type")}
                >
                  Resource Type
                </RequiredLabel>
                <Select
                  value={formData.type}
                  onValueChange={(value: any) => setFormData((prev) => ({ ...prev, type: value }))}
                >
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PDF">PDF</SelectItem>
                    <SelectItem value="VIDEO">Video</SelectItem>
                    <SelectItem value="AUDIO">Audio</SelectItem>
                    <SelectItem value="EBOOK">eBook</SelectItem>
                    <SelectItem value="JOURNAL">Journal</SelectItem>
                    <SelectItem value="RESEARCH_PAPER">Research Paper</SelectItem>
                    <SelectItem value="INSTITUTIONAL_CONTENT">Institutional Content</SelectItem>
                    <SelectItem value="WEBSITE">Website / Online Resource</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="collection">Collection</Label>
                <Select
                  value={formData.collectionId || "none"}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      collectionId: value === "none" ? undefined : value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select collection (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {collections.map((collection) => (
                      <SelectItem key={collection.id} value={collection.id}>
                        {collection.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(isExternalResource || formData.type === "WEBSITE") && (
              <div>
                <RequiredLabel htmlFor="websiteUrl" required>
                  Website URL
                </RequiredLabel>
                <Input
                  id="websiteUrl"
                  type="url"
                  value={formData.websiteUrl || ""}
                  onChange={(e) => setFormData((prev) => ({ ...prev, websiteUrl: e.target.value }))}
                  placeholder="https://example.com/resource"
                  className="mt-1.5"
                  required
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Authors */}
        <Card>
          <CardHeader>
            <CardTitle>Authors</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter author name"
                value={authorInput}
                onChange={(e) => setAuthorInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAuthor();
                  }
                }}
              />
              <Button type="button" onClick={addAuthor}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {formData.authors && formData.authors.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.authors.map((author, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {author}
                    <button
                      type="button"
                      onClick={() => removeAuthor(index)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Publication Details */}
        <Card>
          <CardHeader>
            <CardTitle>Publication Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="publisher">Publisher</Label>
                <Input
                  id="publisher"
                  value={formData.publisher}
                  onChange={(e) => setFormData((prev) => ({ ...prev, publisher: e.target.value }))}
                  placeholder="Enter publisher name"
                />
              </div>
              <div>
                <Label htmlFor="publishedDate">Published Date</Label>
                <Input
                  id="publishedDate"
                  value={formData.publishedDate}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, publishedDate: e.target.value }))
                  }
                  placeholder="e.g., 2024 or 2024-01-15"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Classification & Cataloging */}
        <Card>
          <CardHeader>
            <CardTitle>Classification & Cataloging</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="lcClassification">LC Classification</Label>
                <Input
                  id="lcClassification"
                  value={formData.lcClassification}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, lcClassification: e.target.value }))
                  }
                  placeholder="e.g., QA76.73"
                />
              </div>
              <div>
                <Label htmlFor="callNumber">Call Number</Label>
                <Input
                  id="callNumber"
                  value={formData.callNumber}
                  onChange={(e) => setFormData((prev) => ({ ...prev, callNumber: e.target.value }))}
                  placeholder="e.g., QA76.73.J38 D67 2018"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subjects */}
        <Card>
          <CardHeader>
            <CardTitle>Subjects</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter subject"
                value={subjectInput}
                onChange={(e) => setSubjectInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addSubject();
                  }
                }}
              />
              <Button type="button" onClick={addSubject}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {formData.subjects && formData.subjects.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.subjects.map((subject, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-1">
                    {subject}
                    <button
                      type="button"
                      onClick={() => removeSubject(index)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tags */}
        <Card>
          <CardHeader>
            <CardTitle>Tags</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Enter tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  }
                }}
              />
              <Button type="button" onClick={addTag}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(index)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {showPublicAccessHint && !paidAccessRequired && !formData.isPublic && (
                <PaidAccessVisibilityAlert />
              )}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isPublic"
                  checked={formData.isPublic}
                  disabled={paidAccessRequired}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, isPublic: !!checked }))
                  }
                />
                <Label htmlFor="isPublic" className="cursor-pointer">
                  Make this resource publicly accessible
                  {paidAccessRequired ? " (disabled while paid access is enabled)" : ""}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allowDownload"
                  checked={formData.allowDownload}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, allowDownload: !!checked }))
                  }
                />
                <Label htmlFor="allowDownload" className="cursor-pointer">
                  Allow users to download this resource
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {id && formData.type === "PDF" && !isExternalResource && (
          <PaidAccessFileReplaceNotice show={hasPaidActivity} />
        )}

        {id &&
          formData.type === "PDF" &&
          !isExternalResource &&
          paidAdminLoaded &&
          (canManagePaidAccess || hasPermission("digital.edit")) && (
            <PaidAccessRulesEditor
              digitalResourceId={id}
              initialRules={paidAdminSnapshot?.rules}
              isPublic={formData.isPublic}
              onRequirePaymentChange={(required) => {
                setPaidAccessRequired(required);
                if (required) {
                  setFormData((prev) => ({ ...prev, isPublic: false }));
                  setShowPublicAccessHint(false);
                }
              }}
              onPaidAccessDisabled={() => setShowPublicAccessHint(true)}
              onRestorePublicAccess={async () => {
                if (!id) return;
                setFormData((prev) => ({ ...prev, isPublic: true }));
                setShowPublicAccessHint(false);
                await catalogApi.updateDigitalResource(id, { isPublic: true });
              }}
              onSaved={() => void loadPaidAdminSnapshot()}
            />
          )}

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate("/catalog")}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
